import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, RotateCcw, ShieldCheck } from 'lucide-react'
import { useToast } from '../../../hooks/useToast'
import { Toast } from '../../../components'
import { digitalCertificateService } from '../../../services/erpApi'
import './ErpSettingsHubPage.css'
import './CockpitSettingsPage.css'
import './CertificadosPage.css'

function formatDay(value) {
  const text = String(value || '').slice(0, 10)
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/)
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—'
}

function CertificadosPage() {
  const { toast, showToast, hideToast } = useToast()
  const fileRef = useRef(null)
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [file, setFile] = useState(null)
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const activeCount = items.filter((item) => item.active).length

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const response = await digitalCertificateService.getAll({ limit: 20 })
      setItems(response?.data || [])
    } catch (err) {
      showToast(err.response?.data?.message || 'Não foi possível carregar os certificados', 'error')
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    load()
  }, [load])

  const submit = async (event) => {
    event.preventDefault()
    if (!file || !password) return
    setSaving(true)
    try {
      await digitalCertificateService.upload(file, password, name)
      setFile(null)
      setPassword('')
      setName('')
      if (fileRef.current) fileRef.current.value = ''
      showToast('Certificado enviado. Os próximos documentos saem assinados digitalmente.', 'success')
      await load()
    } catch (err) {
      showToast(err.response?.data?.message || 'Não foi possível enviar o certificado', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="erp-settings-dashboard certificate-page">
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={hideToast} />

      <div className="erp-settings-header">
        <div>
          <div className="certificate-title-row">
            <span className="cockpit-settings-icon-badge certificate-icon-badge">
              <ShieldCheck size={20} />
            </span>
            <h1 className="erp-settings-title">
              Certificado <span className="erp-settings-title-highlight">digital</span>
            </h1>
          </div>
          <p className="erp-settings-subtitle">
            A1 da organização · {activeCount} ativo(s) · {items.length} enviado(s)
          </p>
        </div>
        <div className="erp-settings-header-actions">
          <button
            type="button"
            className="erp-settings-reload-btn"
            onClick={() => load()}
            disabled={loading}
            aria-label="Atualizar certificados"
          >
            <RotateCcw size={16} className={loading ? 'cockpit-settings-spinner' : ''} />
          </button>
        </div>
      </div>

      <section className="cockpit-settings-card certificate-card">
        <h2 className="cockpit-settings-card-title">Enviar certificado</h2>
        <p className="cockpit-settings-card-description">
          O arquivo A1 assina o PDF do prontuário e das evoluções. O certificado novo fica ativo e os anteriores são desativados.
        </p>
        <form className="certificate-form" onSubmit={submit}>
          <label className="cockpit-settings-field certificate-field-wide">
            <span>Nome de exibição</span>
            <input
              className="cockpit-settings-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Opcional. O titular do certificado é usado se ficar vazio."
            />
          </label>
          <label className="cockpit-settings-field">
            <span>Arquivo .pfx ou .p12</span>
            <input
              ref={fileRef}
              className="cockpit-settings-input certificate-file"
              type="file"
              accept=".pfx,.p12,application/x-pkcs12"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
            />
          </label>
          <label className="cockpit-settings-field">
            <span>Senha do certificado</span>
            <input
              className="cockpit-settings-input"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
            />
          </label>
          <div className="certificate-form-actions">
            <button className="certificate-submit" type="submit" disabled={saving || !file || !password}>
              {saving ? <Loader2 size={16} className="cockpit-settings-spinner" /> : <ShieldCheck size={16} />}
              {saving ? 'Enviando...' : 'Enviar certificado'}
            </button>
          </div>
        </form>
      </section>

      <section className="cockpit-settings-card certificate-card">
        <h2 className="cockpit-settings-card-title">Certificados da organização</h2>
        {loading && (
          <p className="cockpit-settings-card-description">Carregando...</p>
        )}
        {!loading && items.length === 0 && (
          <div className="erp-settings-empty certificate-empty">
            <ShieldCheck size={28} />
            <p>Nenhum certificado enviado.</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="certificate-table-wrap">
            <table className="certificate-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Status</th>
                  <th>Validade</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td className="certificate-name">{item.name}</td>
                    <td>
                      <span className={`certificate-status ${item.active ? 'is-on' : 'is-off'}`}>
                        {item.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>{formatDay(item.valid_until)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default CertificadosPage
